import React from 'react';
import { Stack, Text, useTheme } from '@fluentui/react';
import { IStorefrontSection } from '../../../models/Storefront';
import StorefrontSectionEditor from '../Editor/Sections/StorefrontSectionEditor';
import { IStorefrontTemplateLookOption } from '../../../models/StorefrontTemplateApply';


// One section, rendered by the SAME component the pages editor renders it with — not an approximation of it.
//
// The first version of this drew its own little band, title and three grey placeholder tiles. It was cheap and
// it was honest about being a sketch, but a sketch is no use for the only question this step asks: would this
// change do the right thing to MY section? Something that merely suggests the answer cannot settle it. So this
// mounts StorefrontSectionEditor in read-only mode (editMode off, no callbacks), which resolves the section's
// real products and its real card styling and draws exactly what the editor draws.
//
// The "after" is built by applying the same fields the server applies, so what is previewed is what gets
// written — see sectionWithLook below and ApplySectionStyling on the API side; the two lists have to agree.
//
// Compactness comes from SCALING the real thing rather than from drawing less of it: the section renders at
// storefront width and is scaled to fit whatever column it is given, so every property is present and in
// proportion. Callers place the pair side by side, which halves each column's width and therefore the scale —
// small, but a comparison you can actually make in one glance beats a larger one you have to scroll between.

// The width a storefront section is laid out for. The preview renders at this width and is scaled down to
// whatever space it has, so every real property lands at its real proportion — a 24px padding stays a quarter
// of a 96px one — instead of being reflowed into a narrow column, which is what a section squeezed into a
// panel would do and is exactly how a preview stops resembling the page.
const DESIGN_WIDTH = 1000;

// Narrowest a before/after column may get before a pair stacks instead. Two of these plus the gap is the
// breakpoint: wider than about 530px of content and the previews sit side by side; narrower and they go one
// above the other at full width, which keeps them legible rather than scaling them into postage stamps.
// Shared so every surface showing a before/after pair breaks at the same width.
export const MIN_PREVIEW_COLUMN = 260;

/**
 * Renders its children at storefront width and scales the result to fit. Read-only by construction: a scaled
 * subtree's hit targets no longer line up with what is drawn, which is fine here because nothing in a preview
 * is meant to be clicked.
 */
const ScaledToFit: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const outer = React.useRef<HTMLDivElement>(null);
    const inner = React.useRef<HTMLDivElement>(null);
    const [scale, setScale] = React.useState(1);
    const [height, setHeight] = React.useState<number>();

    React.useLayoutEffect(() => {
        const outerEl = outer.current;
        const innerEl = inner.current;
        if (!outerEl || !innerEl) return;

        // The measurement writes a height, and that write resizes an element this same observer is watching.
        // Done synchronously inside the callback that is the textbook cause of "ResizeObserver loop completed
        // with undelivered notifications": the resize lands while notifications are still being delivered. With
        // several previews stretched to a common height in one flex row they also retrigger each other as
        // products load, so the browser sees a burst of them. Deferring the write to the next frame takes it
        // out of the observation cycle, and the equality guards below stop it from ever becoming a real loop.
        let frame = 0;
        const measure = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                if (!outer.current || !inner.current) return;
                // Never scale UP: a section narrower than the space it has should sit at its own size rather
                // than be blown up past the fidelity it actually has.
                const nextScale = Math.min(1, outer.current.clientWidth / DESIGN_WIDTH);
                // The box has to take the SCALED height, or a transform leaves the original height behind as
                // blank space below the preview.
                const nextHeight = inner.current.scrollHeight * nextScale;
                // Rounded before comparing: sub-pixel churn from a reflow would otherwise write a new height
                // every frame and keep the cycle alive forever.
                setScale((current) => (Math.abs(current - nextScale) < 0.001 ? current : nextScale));
                setHeight((current) => (current !== undefined && Math.abs(current - nextHeight) < 0.5
                    ? current
                    : nextHeight));
            });
        };

        measure();
        // Both ends matter: the panel resizing changes the scale, and the section's own content settling
        // (products arriving, an image loading) changes the height.
        const observer = new ResizeObserver(measure);
        observer.observe(outerEl);
        observer.observe(innerEl);
        return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
        };
    }, []);

    return (
        // Three boxes, each earning its place: the outer centres the scaled result, the middle is the
        // FOOTPRINT the scale leaves behind (a transform does not change layout size, so without it the box
        // keeps the untransformed 1000px width and height and the preview sits in the corner of a much larger
        // empty area), and the inner is the section at its real width.
        <div ref={outer} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: DESIGN_WIDTH * scale, height, overflow: 'hidden' }}>
                <div
                    ref={inner}
                    style={{ width: DESIGN_WIDTH, transform: `scale(${scale})`, transformOrigin: 'top left' }}
                >
                    {children}
                </div>
            </div>
        </div>
    );
};

interface ISectionRealPreviewProps {
    /** 'Now' or 'With this template' — rendered above the preview. */
    label: string;
    section: IStorefrontSection;
    /** The storefront the section belongs to, so its products resolve. */
    storefrontId: string;
    /** Dims the frame, for the "now" side when a change is staged. */
    muted?: boolean;
    /** Shown under the preview, e.g. to say a look carries no section styling of its own. */
    note?: string;
}

/**
 * The section as an apply would leave it: the five style-prop bags and the frame scalars taken from the
 * template's look, everything else — title, content, products, placement — untouched.
 *
 * Mirrors StorefrontTemplatesHandler.ApplySectionStyling. If a field is added there it belongs here too, or
 * the preview stops being what gets written.
 */
export const sectionWithLook = (section: IStorefrontSection, look: IStorefrontTemplateLookOption): IStorefrontSection => ({
    ...section,
    styleProps: look.styleProps,
    titleStyleProps: look.titleStyleProps,
    headerStyleProps: look.headerStyleProps,
    footerStyleProps: look.footerStyleProps,
    productStyleProps: look.productStyleProps,
    // The look's page-level overrides (AC-1165). Without these the "after" shows a section restyled only
    // down to its band while the apply would also change its cards, buttons and title block - the preview
    // would promise less than it delivers. Empty, not undefined, matching ApplySectionStyling.
    styleOverrides: look.styleOverrides ?? {},
    width: look.width ?? section.width,
    fullWidthBackground: look.fullWidthBackground,
    verticalAlign: look.verticalAlign,
    hideTitle: look.hideTitle,
});

/** The section with its own styling removed — what clearing it leaves behind. */
export const sectionWithoutStyling = (section: IStorefrontSection): IStorefrontSection => ({
    ...section,
    styleProps: {},
    titleStyleProps: {},
    headerStyleProps: {},
    footerStyleProps: {},
    productStyleProps: {},
    // Mirrors ClearSectionStyling, which clears these too: an override is per-section styling, and leaving
    // it would keep the section looking hand-tuned below the band.
    styleOverrides: {},
});

const SectionRealPreview: React.FC<ISectionRealPreviewProps> = ({ label, section, storefrontId, muted, note }) => {
    const theme = useTheme();
    // The editor is a controlled component; nothing here edits, so changes go nowhere.
    const ignore = React.useCallback(() => undefined, []);

    return (
        // Full height so a pair of these ends up the same size. The two sections differ in padding and borders,
        // so their scaled heights differ, and two frames of visibly different height read as a rendering fault
        // rather than as the difference being shown. The frame grows to whichever side is taller and the
        // shorter one simply has space beneath its section.
        <Stack tokens={{ childrenGap: 4 }} styles={{ root: { minWidth: 0, height: '100%' } }}>
            <Text variant='smallPlus' styles={{ root: { color: theme.palette.neutralSecondary } }}>{label}</Text>
            <Stack.Item grow styles={{ root: { minHeight: 0 } }}>
                <div
                    style={{
                        border: `1px solid ${theme.palette.neutralQuaternary}`,
                        borderRadius: 2,
                        padding: 8,
                        height: '100%',
                        boxSizing: 'border-box',
                        opacity: muted ? 0.75 : 1,
                        // The section paints its own background; the frame must not tint it.
                        background: theme.palette.white,
                    }}
                >
                    <ScaledToFit>
                        <StorefrontSectionEditor
                            section={section}
                            onChange={ignore}
                            editMode={false}
                            storefrontId={storefrontId}
                        />
                    </ScaledToFit>
                </div>
            </Stack.Item>
            {note && (
                <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>{note}</Text>
            )}
        </Stack>
    );
};

export default SectionRealPreview;
